import React from "react";

import { Box, LinearProgress, Typography } from "@mui/material";
import { useCardSize } from "components/Report-generator/context/CardSizeContext";
import type { DataTableCardData, TableRow } from "store/types";

interface Props {
  data?: DataTableCardData;
  loading?: boolean;
}

type Tier = "F" | "M" | "S";

const TIER_STYLES: Record<Tier, { font: number; headerFont: number; padV: number; padH: number }> = {
  F: { font: 13, headerFont: 11, padV: 10, padH: 16 },
  M: { font: 11, headerFont: 9, padV: 8, padH: 12 },
  S: { font: 9, headerFont: 8, padV: 6, padH: 9 }
};

function getTier(widthPercent: number): Tier {
  if (widthPercent >= 60) return "F";
  if (widthPercent >= 38) return "M";
  return "S";
}

export const DataTableCard: React.FC<Props> = ({ data, loading }) => {
  if (loading || !data) {
    return (
      <Box sx={{ p: "20px 16px" }}>
        <LinearProgress sx={{ mb: "4px" }} />
      </Box>
    );
  }

  const { widthPercent } = useCardSize();

  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());

  const toggleExpand = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const tier = getTier(widthPercent);
  const { font: bodyFont, headerFont, padV, padH } = TIER_STYLES[tier];

  const columns = data.columns.map((col) => (typeof col === "string" ? { key: col, title: col } : col));

  if (columns.length === 0) {
    return (
      <Box sx={{ p: "20px 16px" }}>
        {loading && <LinearProgress sx={{ mb: "4px" }} />}
        <Typography sx={{ fontSize: 12, color: "#9CA3AF" }}>No columns in the response</Typography>
      </Box>
    );
  }

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "number") {
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }
    return String(value);
  };

  const renderRow = (row: TableRow, rowIndex: number, depth: number, parentKey: string): React.ReactNode[] => {
    const rows: React.ReactNode[] = [];

    const rowKey = `${parentKey}-${rowIndex}`;
    const hasChildren = !!row.children?.length;
    const isExpanded = expanded.has(rowKey);
    const isGroup = hasChildren;

    rows.push(
      <Box
        component="tr"
        key={rowKey}
        sx={{
          borderBottom: "1px solid #F3F4F6",
          bgcolor: isGroup ? "#F9FAFB" : rowIndex % 2 === 0 ? "#FFFFFF" : "#F9FAFB"
        }}
      >
        {columns.map((col, colIndex) => {
          let cellContent: string;

          if (colIndex === 0) {
            cellContent = row.title || "";
          } else if (row.valueData) {
            cellContent = formatValue(row.valueData[col.key]);
          } else if (typeof row[col.key] !== "undefined") {
            cellContent = formatValue(row[col.key]);
          } else {
            cellContent = "-";
          }

          return (
            <Box
              component="td"
              key={col.key}
              sx={{
                p: `${padV}px ${padH}px`,
                textAlign: colIndex === 0 ? "left" : "right",
                color: colIndex === 0 ? "#111827" : "#374151",
                fontWeight: colIndex === 0 || isGroup ? (isGroup ? 600 : 500) : 400,
                fontSize: bodyFont,
                whiteSpace: "normal",
                wordBreak: "break-word",
                ...(depth > 0 && colIndex === 0 ? { pl: `${padH + depth * 12}px` } : {})
              }}
            >
              {colIndex === 0 && hasChildren ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  {/* toggle icon */}
                  <Box
                    onClick={() => toggleExpand(rowKey)}
                    sx={{
                      cursor: "pointer",
                      fontSize: 12,
                      width: 14,
                      userSelect: "none"
                    }}
                  >
                    {isExpanded ? "▾" : "▸"}
                  </Box>

                  {/* clickable text */}
                  <Box
                    onClick={() => toggleExpand(rowKey)}
                    sx={{
                      cursor: "pointer",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    {cellContent}
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    pl: hasChildren === false && depth === 0 && row.children ? "18px" : undefined
                  }}
                >
                  {cellContent}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    );

    if (hasChildren && isExpanded) {
      row.children!.forEach((child, childIndex) => {
        rows.push(...renderRow(child, childIndex, depth + 1, rowKey));
      });
    }

    return rows;
  };

  return (
    <Box data-table-card sx={{ overflowX: "hidden", p: "4px 0" }}>
      {loading && <LinearProgress sx={{ mb: "4px" }} />}

      <Box
        component="table"
        sx={{
          width: "100%",
          tableLayout: "fixed",
          borderCollapse: "collapse",
          fontSize: bodyFont
        }}
      >
        <colgroup>
          {columns.map((col) => (
            <col key={col.key} style={{ width: `${(100 / columns.length).toFixed(2)}%` }} />
          ))}
        </colgroup>

        <thead>
          <Box component="tr" sx={{ borderBottom: "2px solid #E5E7EB" }}>
            {columns.map((col, colIndex) => (
              <Box
                component="th"
                key={`${col.key}-${colIndex}`}
                sx={{
                  p: `${padV}px ${padH}px`,
                  textAlign: colIndex === 0 ? "left" : "right",
                  fontSize: headerFont,
                  fontWeight: 700,
                  color: "#6B7280",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                {col.title}
              </Box>
            ))}
          </Box>
        </thead>

        <tbody>
          {data.data.map((row, rowIndex) => renderRow(row, rowIndex, 0, "root"))}

          {data.data.length === 0 && (
            <tr>
              <Box
                component="td"
                colSpan={columns.length}
                sx={{
                  p: "24px 16px",
                  textAlign: "center",
                  color: "#9CA3AF",
                  fontSize: 13
                }}
              >
                No data available
              </Box>
            </tr>
          )}
        </tbody>
      </Box>
    </Box>
  );
};
